// src/layouts/openPages/live/Live_Lite.js
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  CircularProgress,
  Typography,
  Paper,
  Alert,
  Button,
  Modal,
} from '@mui/material';
import { getThing, startThingUpdatesNoJourney, stopUpdates } from './LiveApis';
import Openlayer from './openlayer';

function Live_Lite() {
  // ──────────────────────────────────────────────────────────────
  // 1. State
  // ──────────────────────────────────────────────────────────────
  const [thingModel, setThingModel] = useState(null);
  const [error, setError] = useState(null);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [iframeLink, setIframeLink] = useState('');

  // **NEW** – device status (you can feed it from the API or from thingModel)
  const [deviceStatus, setDeviceStatus] = useState('Loading…');

  // ──────────────────────────────────────────────────────────────
  // 2. Get user location
  // ──────────────────────────────────────────────────────────────
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setUserLocation({
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
          });
        },
        () => setUserLocation(null),
        { enableHighAccuracy: true, timeout: 10000 }
      );
    }
  }, []);

 // src/layouts/openPages/live/Live_Lite.js

useEffect(() => {
  getThing(
    (data) => {
      setThingModel(data);
      setError(null);

      // Pass both location and status callbacks
      startThingUpdatesNoJourney(
        (loc) => setCurrentLocation(loc),
        (status) => setDeviceStatus(status) // <-- NEW: Update status
      );
    },
    (err) => {
      setError('Failed to load tracker.');
      setDeviceStatus('Disconnected');
    }
  );

  const timer = setTimeout(() => setIsLoading(true), 5000);
  return () => {
    clearTimeout(timer);
    stopUpdates();
  };
}, []);

  // ──────────────────────────────────────────────────────────────
  // 4. Google-Maps embed modal
  // ──────────────────────────────────────────────────────────────
  const openGoogleMapsModal = useCallback(() => {
    if (!userLocation || !currentLocation) return;

    const origin = `${userLocation.latitude},${userLocation.longitude}`;
    const destination = `${currentLocation.latitude},${currentLocation.longitude}`;

    const glink = `https://www.google.com/maps/embed?pb=!1m26!1m12!1m3!1d952990.5372896822!2d77.48398416359312!3d21.08786269702407!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!4m11!3e0!4m4!2s${currentLocation.latitude}%2C${currentLocation.longitude}!3m2!1d${currentLocation.latitude}!2d${currentLocation.longitude}!4m4!2s${userLocation.latitude}%2C${userLocation.longitude}!3m2!1d${userLocation.latitude}!2d${userLocation.longitude}!5e0!3m2!1sen!2sin!4v1668452361266!5m2!1sen!2sin`;

    setIframeLink(glink);
    setModalOpen(true);
  }, [userLocation, currentLocation]);

  // ──────────────────────────────────────────────────────────────
  // 5. UI States
  // ──────────────────────────────────────────────────────────────
  if (thingModel === null && error === null && !isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
        <Typography ml={2}>Loading tracker…</Typography>
      </Box>
    );
  }

  if (thingModel === null && error === null && isLoading) {
    return (
      <Paper elevation={3} sx={{ p: 3, textAlign: 'center', mt: 4 }}>
        <Typography variant="h6" gutterBottom>
          If loading takes too long,
        </Typography>
        <Button variant="contained" onClick={() => window.location.reload()} sx={{ mt: 2 }}>
          Refresh
        </Button>
      </Paper>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        <Typography>{error}</Typography>
        <Button size="small" onClick={() => window.location.reload()} sx={{ mt: 1 }}>
          Refresh
        </Button>
      </Alert>
    );
  }

  // ──────────────────────────────────────────────────────────────
  // 6. Main UI (Map + Status + Modal)
  // ──────────────────────────────────────────────────────────────
  return (
    <Box sx={{ width: '100%', py: 2 }}>
    

      {/* ---------- Map ---------- */}
      <Paper elevation={2} sx={{ position: 'relative', overflow: 'hidden', borderRadius: 2 }}>
        <Openlayer position={currentLocation} userLocation={userLocation} />

        {/* Distance From Me Button */}
        <Button
          variant="contained"
          color="secondary"
          onClick={openGoogleMapsModal}
          disabled={!userLocation || !currentLocation}
          sx={{
            position: 'absolute',
            top: 16,
            left: 16,
            zIndex: 10,
            color: '#fff',
            px: 3,
            py: 1,
            fontWeight: 'bold',
            minWidth: 180,
            borderRadius: 2,
            boxShadow: 3,
          }}
        >
          Distance From Me
        </Button>
      </Paper>

      {/* ---------- Google Maps Route Modal ---------- */}
      <Modal open={modalOpen} onClose={() => setModalOpen(false)}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '95%', sm: '80%', md: '70%' },
            maxWidth: 900,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            p: 3,
          }}
        >
          <Typography variant="h6" gutterBottom>
            Route from You to Vehicle
          </Typography>

          <iframe
            id="gmaps-iframe"
            title="Route"
            src={iframeLink}
            width="100%"
            height="500"
            style={{ border: 0, borderRadius: 8 }}
            loading="lazy"
            allowFullScreen
          />

          <Box sx={{ mt: 2, textAlign: 'right' }}>
            <Button onClick={() => setModalOpen(false)} variant="outlined" style={{color:'black'}}>
              Close
            </Button>
          </Box>
        </Box>
      </Modal>

        {/* ---------- Device Status Badge ---------- */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          mb: 2,
          mt:2
        }}
      >
        <Box
          sx={{
            background:
              deviceStatus === 'Device Connected' ? '#3cc88266' : '#21E01366',
            color: '#fff',
            fontWeight: 'bold',
            padding: '8px 16px',
            borderRadius: 2,
            textAlign: 'center',
            minWidth: 180,
          }}
        >
          {deviceStatus}
        </Box>
      </Box>
    </Box>
  );
}

export default Live_Lite;